// Pop-up notifications — full-screen announcements shown over the app, view-and-dismiss only.
// Ported from `support proto/admin-popups.html`; the prototype's demo data is gone, every number
// on this page comes from the impressions the app records.
import React, { useCallback, useEffect, useState } from 'react';
import {
  createPopup,
  deletePopup,
  fetchPopupStats,
  fetchPopups,
  setPopupActive,
  updatePopup,
} from '../../api/popups';
import { useNotification } from '../../hooks/useNotification';
import Campaigns from './Campaigns';
import Compose from './Compose';
import PhonePreview from './PhonePreview';
import Templates from './Templates';
import { DEFAULT_CTA, DEFAULT_FOCUS, audienceLabel, deltaPct, num } from './format';
import '../../assets/css/popups.css';

const BLANK = {
  format: 'both',
  placement: 'center',
  imageFocus: DEFAULT_FOCUS,
  image: '',
  title: '',
  body: '',
  cta: DEFAULT_CTA,
  mode: 'segment',
  gender: 'all',
  country: 'all',
  city: '',
  members: [],
  perDay: 2,
  seconds: 8,
  endsAt: '',
};

const Delta = ({ current, previous, flat }) => {
  const delta = deltaPct(current, previous);
  if (delta === null) return <i className="flat">{flat}</i>;
  return (
    <i className={delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}>
      {`${delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} ${Math.abs(delta)}% vs last week`}
    </i>
  );
};

const PopupNotifications = () => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [draft, setDraft] = useState(BLANK);
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // The campaign Compose is editing, or null when it is composing a new one.
  const [editing, setEditing] = useState(null);

  const patch = (fields) => setDraft((prev) => ({ ...prev, ...fields }));

  // The message a failed request came back with, rather than a generic one: the server's
  // validation says which field is wrong.
  const fail = useCallback(
    (error, fallback) => {
      console.error('popups:', error);
      showErrorNotification(error?.response?.data?.message || fallback);
    },
    [showErrorNotification],
  );

  // allSettled, not all: these are three independent panels, and `Promise.all` rejects on the
  // first failure — so one bad stats response blanked the campaign table too, which reads as "no
  // campaigns yet" rather than as the error it is. Each panel now fails alone and says so.
  const load = useCallback(async () => {
    const [statsRes, campaignRes, templateRes] = await Promise.allSettled([
      fetchPopupStats(7),
      fetchPopups('campaign'),
      fetchPopups('template'),
    ]);
    if (statsRes.status === 'fulfilled' && statsRes.value) setStats(statsRes.value);
    if (campaignRes.status === 'fulfilled') setCampaigns(campaignRes.value || []);
    if (templateRes.status === 'fulfilled') setTemplates(templateRes.value || []);

    const broken = [
      campaignRes.status === 'rejected' && 'campaigns',
      templateRes.status === 'rejected' && 'templates',
      statsRes.status === 'rejected' && 'the tiles',
    ].filter(Boolean);
    if (broken.length) {
      const first = [campaignRes, templateRes, statsRes].find((r) => r.status === 'rejected');
      fail(first.reason, `Could not load ${broken.join(' or ')}`);
    }
  }, [fail]);

  useEffect(() => { load(); }, [load]);

  // Load a campaign back into Compose. Its audience comes along read-only — see updatePopup.
  const edit = (campaign) => {
    setEditing(campaign);
    setDraft({
      format: campaign.format || 'both',
      placement: campaign.placement || 'center',
      imageFocus: campaign.imageFocus || DEFAULT_FOCUS,
      image: campaign.image || '',
      title: campaign.title || '',
      body: campaign.body || '',
      cta: campaign.cta || DEFAULT_CTA,
      mode: campaign.audience?.mode || 'segment',
      gender: campaign.audience?.gender || 'all',
      country: campaign.audience?.country || 'all',
      city: campaign.audience?.city || '',
      members: [],
      perDay: campaign.perDay ?? 2,
      seconds: campaign.seconds ?? 8,
      endsAt: campaign.endsAt ? String(campaign.endsAt).slice(0, 10) : '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(BLANK);
  };

  const saveEdit = async () => {
    setLaunching(true);
    try {
      // Only what an edit may change. The audience and its snapshot are deliberately absent.
      await updatePopup(editing._id, {
        placement: draft.placement,
        imageFocus: draft.imageFocus,
        title: draft.title,
        body: draft.body,
        cta: draft.cta,
        image: draft.image,
        perDay: draft.perDay,
        seconds: draft.seconds,
        endsAt: draft.endsAt || null,
      });
      showSuccessNotification('Campaign updated — the next member to see it gets the new copy.');
      cancelEdit();
      load();
    } catch (error) {
      fail(error, 'Could not update the campaign');
    } finally {
      setLaunching(false);
    }
  };

  const remove = async (popup, what) => {
    setBusyId(popup._id);
    try {
      await deletePopup(popup._id);
      showSuccessNotification(`${what} deleted, along with its impressions.`);
      if (editing?._id === popup._id) cancelEdit();
      load();
    } catch (error) {
      fail(error, `Could not delete the ${what.toLowerCase()}`);
    } finally {
      setBusyId(null);
    }
  };

  const launch = async (reach) => {
    setLaunching(true);
    try {
      await createPopup({
        kind: 'campaign',
        format: draft.format,
        placement: draft.placement,
        imageFocus: draft.imageFocus,
        title: draft.title,
        body: draft.body,
        cta: draft.cta,
        image: draft.image,
        audience: {
          mode: draft.mode,
          gender: draft.gender,
          country: draft.country,
          city: draft.city,
          userIds: draft.members.map((m) => m._id),
        },
        perDay: draft.perDay,
        seconds: draft.seconds,
        endsAt: draft.endsAt || null,
      });
      const who = draft.mode === 'members' ? audienceLabel(draft) : `${num(reach ?? 0)} members · ${audienceLabel(draft)}`;
      showSuccessNotification(`Pop-up is live for ${who} — every render counts toward “seen”.`);
      setDraft(BLANK);
      load();
    } catch (error) {
      fail(error, 'Could not launch the pop-up');
    } finally {
      setLaunching(false);
    }
  };

  const saveTemplate = async (template, templateId) => {
    setSavingTemplate(true);
    try {
      if (templateId) {
        // No `kind` and no audience: a template's trigger is its audience, and its kind never
        // changes. The format follows the content, which the server recomputes for itself.
        await updatePopup(templateId, {
          name: template.name,
          title: template.title,
          body: template.body,
          cta: template.cta,
          image: template.image,
          placement: template.placement,
          imageFocus: template.imageFocus,
          trigger: template.trigger,
          delayHours: template.delayHours,
        });
        showSuccessNotification(`Template “${template.name}” updated.`);
      } else {
        await createPopup({ kind: 'template', ...template });
        showSuccessNotification(`Template “${template.name}” saved.`);
      }
      load();
      return true;
    } catch (error) {
      fail(error, 'Could not save the template');
      return false;
    } finally {
      setSavingTemplate(false);
    }
  };

  // One handler for both tables: ending a campaign and switching a template off are the same
  // write, and the row it belongs to is the one that goes busy.
  const toggle = async (popup, setRows, message) => {
    setBusyId(popup._id);
    try {
      const updated = await setPopupActive(popup._id, !popup.active);
      setRows((rows) => (rows || []).map((r) => (r._id === popup._id ? { ...r, active: updated.active } : r)));
      showSuccessNotification(message(updated.active));
      if (popup.kind === 'campaign') fetchPopupStats(7).then(setStats).catch(() => {});
    } catch (error) {
      fail(error, 'Could not update the pop-up');
    } finally {
      setBusyId(null);
    }
  };

  const reachedPct = stats?.reachableMembers
    ? Math.round((stats.uniqueViewers / stats.reachableMembers) * 100)
    : 0;

  return (
    <div className="pn">
      <div className="page-h"><h1>Pop-up notifications</h1></div>
      <p className="page-sub">
        Full-screen announcements shown over the app — members can only view and dismiss, never reply.
        The one number that matters here is <b>how many people saw it</b>: every impression is counted
        the moment the pop-up renders.
      </p>

      <div className="tiles">
        <div className="tile">
          <b>{num(stats?.liveCampaigns)}</b>
          <span>Live campaigns</span>
          <i className="flat">{`— of ${num(stats?.campaignsThisMonth)} this month`}</i>
        </div>
        <div className="tile">
          <b>{num(stats?.seen)}</b>
          <span>Pop-ups seen · 7d</span>
          <Delta current={stats?.seen} previous={stats?.seenPrev} flat="no week to compare yet" />
        </div>
        <div className="tile">
          <b>{num(stats?.uniqueViewers)}</b>
          <span>Unique viewers · 7d</span>
          <Delta current={stats?.uniqueViewers} previous={stats?.uniquePrev} flat="no week to compare yet" />
        </div>
        <div className="tile">
          <b>{`${reachedPct}%`}</b>
          <span>Of reachable members</span>
          <i className="flat">{`${num(stats?.reachableMembers)} can be reached`}</i>
        </div>
      </div>

      <div className="grid">
        <div>
          <Compose
            draft={draft}
            patch={patch}
            editing={editing}
            onLaunch={launch}
            onSave={saveEdit}
            onClear={editing ? cancelEdit : () => setDraft(BLANK)}
            launching={launching}
          />
          <Campaigns
            campaigns={campaigns}
            busyId={busyId}
            editingId={editing?._id}
            onEdit={edit}
            onDelete={(c) => remove(c, 'Campaign')}
            onEnd={(c) => toggle(c, setCampaigns, () => 'Campaign ended — it stops showing now.')}
          />
          <Templates
            templates={templates}
            saving={savingTemplate}
            busyId={busyId}
            onSave={saveTemplate}
            onDelete={(t) => remove(t, 'Template')}
            onToggle={(t) => toggle(t, setTemplates, (active) => `Template “${t.name}” turned ${active ? 'on' : 'off'}.`)}
          />
        </div>
        <div className="phone-col">
          <PhonePreview
            format={draft.format}
            placement={draft.placement}
            imageFocus={draft.imageFocus}
            onFocusChange={(imageFocus) => patch({ imageFocus })}
            image={draft.image}
            title={draft.title}
            body={draft.body}
            cta={draft.cta}
          />
        </div>
      </div>
    </div>
  );
};

export default PopupNotifications;
